/* ========================================
   GLASSMORPHISM JAVASCRIPT
   Modern glass UI interactions and animations
   ======================================== */

class GlassmorphismTheme {
  constructor() {
    this.init();
  }

  init() {
    this.setupBackgroundShapes();
    this.setupScrollReveal();
    this.setupNavbarEffects();
    this.setupHoverEffects();
    this.setupLoadingStates();
    this.setupParallaxEffects();
    this.setupGlassMorphing();
  }

  /* ========================================
     BACKGROUND SHAPES
   ======================================== */
  setupBackgroundShapes() {
    // Create floating background shapes
    const shapesContainer = document.createElement('div');
    shapesContainer.className = 'glass-bg-shapes';
    document.body.appendChild(shapesContainer);

    // Create multiple shapes
    for (let i = 0; i < 5; i++) {
      const shape = document.createElement('div');
      shape.className = `glass-bg-shape glass-bg-shape--${i + 1}`;
      
      // Random positioning and animation delays
      const randomX = Math.random() * 100;
      const randomY = Math.random() * 100;
      const randomSize = Math.random() * 200 + 100;
      const randomDelay = Math.random() * 20;
      
      shape.style.left = `${randomX}%`;
      shape.style.top = `${randomY}%`;
      shape.style.width = `${randomSize}px`;
      shape.style.height = `${randomSize}px`;
      shape.style.animationDelay = `-${randomDelay}s`;
      
      shapesContainer.appendChild(shape);
    }
  }

  /* ========================================
     SCROLL REVEAL ANIMATIONS
   ======================================== */
  setupScrollReveal() {
    const revealElements = document.querySelectorAll('.glass-scroll-reveal');
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    revealElements.forEach(element => {
      observer.observe(element);
    });
  }

  /* ========================================
     NAVBAR EFFECTS
   ======================================== */
  setupNavbarEffects() {
    const navbar = document.querySelector('.header-wrapper');
    if (!navbar) return;

    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateNavbar = () => {
      const scrollY = window.scrollY;
      
      if (scrollY > 100) {
        navbar.classList.add('glass-nav--scrolled');
      } else {
        navbar.classList.remove('glass-nav--scrolled');
      }

      // Hide/show navbar on scroll
      if (scrollY > lastScrollY && scrollY > 200) {
        navbar.style.transform = 'translateY(-100%)';
      } else {
        navbar.style.transform = 'translateY(0)';
      }

      lastScrollY = scrollY;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateNavbar);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ========================================
     HOVER EFFECTS
   ======================================== */
  setupHoverEffects() {
    // Glass card hover effects
    const glassCards = document.querySelectorAll('.glass-card, .card');
    glassCards.forEach(card => {
      card.addEventListener('mouseenter', () => {
        card.style.transform = 'translateY(-8px) scale(1.02)';
        card.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(0, 198, 255, 0.3)';
      });

      card.addEventListener('mouseleave', () => {
        card.style.transform = 'translateY(0) scale(1)';
        card.style.boxShadow = '';
      });
    });

    // Glass button hover effects
    const glassButtons = document.querySelectorAll('.glass-button, .button');
    glassButtons.forEach(button => {
      button.addEventListener('mouseenter', () => {
        button.style.transform = 'translateY(-2px)';
        button.style.boxShadow = '0 8px 25px rgba(0, 198, 255, 0.4)';
      });

      button.addEventListener('mouseleave', () => {
        button.style.transform = 'translateY(0)';
        button.style.boxShadow = '';
      });
    });

    // Navigation link hover effects
    const navLinks = document.querySelectorAll('.list-menu__item--link, .glass-nav__link');
    navLinks.forEach(link => {
      link.addEventListener('mouseenter', () => {
        link.style.color = 'var(--accent-cyan)';
        link.style.transform = 'translateY(-2px)';
      });

      link.addEventListener('mouseleave', () => {
        link.style.color = '';
        link.style.transform = 'translateY(0)';
      });
    });
  }

  /* ========================================
     LOADING STATES
   ======================================== */
  setupLoadingStates() {
    // Add loading animation to images
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      img.addEventListener('load', () => {
        img.classList.add('glass-animate--fade-in');
      });
    });

    // Add loading animation to sections
    const sections = document.querySelectorAll('.section');
    sections.forEach(section => {
      section.classList.add('glass-animate--slide-in');
    });
  }

  /* ========================================
     PARALLAX EFFECTS
   ======================================== */
  setupParallaxEffects() {
    const parallaxElements = document.querySelectorAll('.glass-bg-shape');
    
    const updateParallax = () => {
      const scrollY = window.scrollY;
      
      parallaxElements.forEach((element, index) => {
        const speed = 0.5 + (index * 0.1);
        const yPos = scrollY * speed;
        element.style.transform = `translateY(${yPos}px)`;
      });
    };

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ========================================
     GLASS MORPHING EFFECTS
   ======================================== */
  setupGlassMorphing() {
    // Create morphing glass effect on mouse move
    const glassElements = document.querySelectorAll('.glass-panel, .glass-card, .glass-section');
    
    document.addEventListener('mousemove', (e) => {
      const mouseX = e.clientX / window.innerWidth;
      const mouseY = e.clientY / window.innerHeight;
      
      glassElements.forEach(element => {
        const rect = element.getBoundingClientRect();
        const elementX = (rect.left + rect.width / 2) / window.innerWidth;
        const elementY = (rect.top + rect.height / 2) / window.innerHeight;
        
        const distance = Math.sqrt(
          Math.pow(mouseX - elementX, 2) + Math.pow(mouseY - elementY, 2)
        );
        
        if (distance < 0.3) {
          const intensity = (0.3 - distance) / 0.3;
          element.style.transform = `perspective(1000px) rotateX(${mouseY * 10 * intensity}deg) rotateY(${mouseX * 10 * intensity}deg)`;
          element.style.boxShadow = `0 ${20 * intensity}px ${40 * intensity}px rgba(0, 0, 0, 0.3), 0 0 ${20 * intensity}px rgba(0, 198, 255, ${0.3 * intensity})`;
        } else {
          element.style.transform = '';
          element.style.boxShadow = '';
        }
      });
    });
  }

  /* ========================================
     UTILITY METHODS
   ======================================== */
  
  // Add glass effect to any element
  addGlassEffect(element) {
    element.classList.add('glass-panel');
  }

  // Remove glass effect from element
  removeGlassEffect(element) {
    element.classList.remove('glass-panel');
  }

  // Create glass morphing animation
  createGlassMorph(element, duration = 1000) {
    element.style.transition = `all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
    element.classList.add('glass-animate--glow');
    
    setTimeout(() => {
      element.classList.remove('glass-animate--glow');
    }, duration);
  }

  // Create glass ripple effect
  createGlassRipple(element, event) {
    const ripple = document.createElement('div');
    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;
    
    ripple.style.cssText = `
      position: absolute;
      width: ${size}px;
      height: ${size}px;
      left: ${x}px;
      top: ${y}px;
      background: radial-gradient(circle, rgba(0, 198, 255, 0.3) 0%, transparent 70%);
      border-radius: 50%;
      transform: scale(0);
      animation: glassRipple 0.6s ease-out;
      pointer-events: none;
      z-index: 1000;
    `;
    
    element.style.position = 'relative';
    element.appendChild(ripple);
    
    setTimeout(() => {
      ripple.remove();
    }, 600);
  }
}

// CSS for ripple animation
const rippleCSS = `
@keyframes glassRipple {
  to {
    transform: scale(2);
    opacity: 0;
  }
}
`;

// Inject ripple CSS
const style = document.createElement('style');
style.textContent = rippleCSS;
document.head.appendChild(style);

// Initialize glassmorphism theme when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.glassmorphismTheme = new GlassmorphismTheme();
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GlassmorphismTheme;
}
